import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { visitorPing } from '../lib/api';
import { useAuth } from '../lib/auth';

const SESSION_ID_KEY = 'winluxor_visitor_session_id';

function getOrCreateSessionId() {
  let id = localStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    // Lightweight random session ID (32 hex chars)
    id = 'v_' + Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    try { localStorage.setItem(SESSION_ID_KEY, id); } catch { /* ignore */ }
  }
  return id;
}

/**
 * Invisible component — mount once near the App root. Pings the backend every
 * 30 seconds while the page is open so admins can see live visitors. Also pings
 * on route change to track which pages get views.
 *
 * Privacy: no PII collected (just an opaque random session ID stored locally).
 *
 * Excluded from tracking:
 *  - All /admin/* pages
 *  - /diagnostic
 *  - Any user with role=admin (avoids contaminating analytics when admin
 *    tests the public site)
 */
export default function VisitorTracker() {
  const location = useLocation();
  const { isAdmin, user } = useAuth();
  const intervalRef = useRef(null);
  const sessionIdRef = useRef(null);

  if (!sessionIdRef.current) {
    sessionIdRef.current = getOrCreateSessionId();
  }

  // Skip tracking on technical paths AND for admin users (so they don't pollute stats)
  const skipPaths = ['/diagnostic', '/admin'];
  const isAdminUser = !!isAdmin || (user && user.role === 'admin');
  const shouldTrack = !isAdminUser && !skipPaths.some((p) => location.pathname.startsWith(p));

  const sendPing = () => {
    if (!shouldTrack) return;
    try {
      visitorPing({
        session_id: sessionIdRef.current,
        path: location.pathname + location.search,
        referrer: document.referrer || '',
        lang: navigator.language || '',
        screen_w: window.innerWidth,
        screen_h: window.innerHeight,
      }).catch(() => { /* silent */ });
    } catch { /* ignore */ }
  };

  // Ping on each route change
  useEffect(() => {
    sendPing();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAdminUser]);

  // Heartbeat: re-ping every 30s if tab is visible
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!document.hidden && shouldTrack) sendPing();
    }, 30000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrack]);

  return null;
}
